import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'SlotPricingEditor' }

import { computed, getCurrentInstance, onMounted, ref, set } from 'vue'
import { validateSlotPricing } from '@/util/slotPricingValidation'

interface Option { id: number; name: string }
interface Tier { min_players: number | null; max_players: number | null; price: number | string | null }
interface Category {
  category: string
  pricing: Tier[]
  minimum_participants: number | null
  maximum_participants: number | null | undefined
  default_participants: number | null
}

interface DurationSet {
  duration_minutes: number
  is_default: boolean
  position: number
  pricing_categories: Category[]
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    slotData: null,
    pricingTypes: null,
    reservationStyles: null,
    valueTypes: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  slotData: Record<string, any>
  pricingTypes: Option[]
  reservationStyles: Option[]
  valueTypes: Option[]
}

// Shared custom pricing editor for a slot or slot template. The `slotData` object
// is edited in place (same convention as the booking drawer views); the host
// keeps ownership of the object and decides when to persist it.


const instance = getCurrentInstance()
// Errors keyed per duration set: 'default' for the default duration, else the minutes
const errorsBySet = ref<Record<string, Record<number, any>>>({})

onMounted(() => {
  if (props.slotData.is_custom_pricing_enabled) seedDefaultCategory()
})

// Multiple durations. The default duration's categories stay in slotData.pricing_categories;
// every other duration owns its own array under slotData.durations[n].pricing_categories.
const defaultCategories = computed<Category[]>(() => {
  if (!Array.isArray(props.slotData.pricing_categories)) {
    set(props.slotData, 'pricing_categories', [])
  }
  return props.slotData.pricing_categories
})

const durations = computed<DurationSet[]>(() => {
  if (!Array.isArray(props.slotData.durations)) {
    set(props.slotData, 'durations', [])
  }
  return props.slotData.durations
})

const activeMinutes = computed<number | null>(() => {
  const v = props.slotData.active_duration_minutes
  return v === null || v === undefined ? null : Number(v)
})

const activeSet = computed<DurationSet | null>(() => {
  if (activeMinutes.value === null) return null
  return durations.value.find(d => Number(d.duration_minutes) === activeMinutes.value && !d.is_default) || null
})

const activeKey = computed(() => (activeSet.value ? String(activeSet.value.duration_minutes) : 'default'))

const categories = computed<Category[]>(() => {
  if (activeSet.value) {
    if (!Array.isArray(activeSet.value.pricing_categories)) set(activeSet.value, 'pricing_categories', [])
    return activeSet.value.pricing_categories
  }
  return defaultCategories.value
})

const errors = computed<Record<number, any>>(() => errorsBySet.value[activeKey.value] || {})

function setField(field: string, value: any) {
  set(props.slotData, field, value)
  if (field === 'is_custom_pricing_enabled' && value) seedDefaultCategory()
}

function ensureDefaultDuration() {
  if (!durations.value.some(d => d.is_default)) {
    durations.value.unshift({ duration_minutes: 60, is_default: true, position: 0, pricing_categories: [] })
  }
}

function onDurationsToggle(on: boolean) {
  set(props.slotData, 'enable_multiple_durations', !!on)
  if (on) ensureDefaultDuration()
  set(props.slotData, 'active_duration_minutes', null)
}

function selectDuration(minutes: number | null) {
  set(props.slotData, 'active_duration_minutes', minutes)
}

function addDuration(minutes: number) {
  ensureDefaultDuration()
  durations.value.push({
    duration_minutes: minutes,
    is_default: false,
    position: durations.value.length,
    pricing_categories: JSON.parse(JSON.stringify(defaultCategories.value))
  })
  set(props.slotData, 'active_duration_minutes', minutes)
}

function removeDuration(minutes: number) {
  const idx = durations.value.findIndex(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
  if (idx < 0) return
  durations.value.splice(idx, 1)
  if (activeMinutes.value === Number(minutes)) set(props.slotData, 'active_duration_minutes', null)
}

function setDefaultDuration(minutes: number) {
  const next = durations.value.find(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
  const current = durations.value.find(d => d.is_default)
  if (!next) return
  // The legacy array always holds the default duration's categories, so the two sets swap
  const nextCategories = next.pricing_categories
  set(next, 'pricing_categories', [])
  set(next, 'is_default', true)
  if (current) {
    set(current, 'is_default', false)
    set(current, 'pricing_categories', defaultCategories.value)
  }
  set(props.slotData, 'pricing_categories', nextCategories)
  set(props.slotData, 'active_duration_minutes', null)
  errorsBySet.value = {}
}

// A slot with custom pricing on and no categories has nothing to price; seed
// the same default category the schedule pages use for new slots.
function seedDefaultCategory() {
  if (defaultCategories.value.length > 0) return
  defaultCategories.value.push({
    category: 'Person',
    pricing: [{ min_players: 1, max_players: 2, price: 0 }],
    minimum_participants: 0,
    maximum_participants: null,
    default_participants: 0
  })
}

function toNumber(v: any): number | null {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}







/** Validate every duration's pricing; highlights errors, switches to the first failing set and notifies. Returns true when valid. */
function validate(): boolean {
  const enabled = !!props.slotData.is_custom_pricing_enabled
  const nextErrors: Record<string, Record<number, any>> = {}
  let valid = true
  let firstFailing: number | null | undefined = undefined
  const defaultResult = validateSlotPricing({ enabled, categories: defaultCategories.value })
  nextErrors['default'] = defaultResult.errors
  if (!defaultResult.valid) { valid = false; firstFailing = null }
  if (enabled && props.slotData.enable_multiple_durations) {
    for (const d of durations.value) {
      if (d.is_default) continue
      const r = validateSlotPricing({ enabled, categories: d.pricing_categories || [] })
      nextErrors[String(d.duration_minutes)] = r.errors
      if (!r.valid) {
        valid = false
        if (firstFailing === undefined) firstFailing = Number(d.duration_minutes)
      }
      if ((d.pricing_categories || []).length === 0) {
        valid = false
        if (firstFailing === undefined) firstFailing = Number(d.duration_minutes)
      }
    }
  }
  errorsBySet.value = nextErrors
  const result = { valid }
  if (!valid && firstFailing !== undefined) set(props.slotData, 'active_duration_minutes', firstFailing)
  if (!result.valid && instance) {
    (instance.proxy as any).$notify({
      type: 'danger',
      message: 'Fix the highlighted pricing errors before saving',
      icon: 'tim-icons icon-alert-circle-exc',
      timeout: 4000
    })
  }
  return result.valid
}

expose({ validate })

return { __sfc: true,props, instance, errorsBySet, defaultCategories, durations, activeMinutes, activeSet, activeKey, categories, errors, setField, ensureDefaultDuration, onDurationsToggle, selectDuration, addDuration, removeDuration, setDefaultDuration, seedDefaultCategory, toNumber, validate }
}

})